import Vue from 'vue';
import {escapeHtml} from "@/app/utils/StringUtils";
import VuexStore from "@/store/vuex/VuexStore";

const VULNERABILITY_SEGMENT_ID = 210833;

const vulnerabilityPillars = {};

const vulnerability = Vue.observable({
    title: "Vulnerability",
    description: "Identify customer vulnerabilities to meet regulatory expectations.",
    expanded:
        `Vulnerability pillars help financial institutions accurately recognise, record, 
            and respond to vulnerability. We empower teams to deliver fair, compliant and sensitive customer service every time.`,
    image: require("../assets/vulnerability.png"),
    metrics: [],
    available: null,
});

export default vulnerability;

let initPromise = null;

export async function init() {
    if (initPromise) return initPromise;

    initPromise = VuexStore.dispatch('refreshTags');
    await initPromise;

    const segment = VuexStore.getters.idToTag.get(VULNERABILITY_SEGMENT_ID);

    if (!segment) {
        vulnerability.available = false;
        return;
    }

    vulnerability.available = true;

    const children = segment?.children ?? [];

    for (const childId of children) {
        const tag = VuexStore.getters.idToTag.get(childId);
        if (tag) {
            vulnerabilityPillars[tag.name] = childId;
        }
    }

    function getVulnerabilityColourSettings(yAxis, geometry) {
        return {
            yAxis,
            geometry,
            "colour-palette": "custom",
            "colour-palette-custom": ["#2e5962", "#4c7b86", "#6a9daa", "#8ec1ce", "#c3e3eb", "#ada9a9"],
            "colour-index": 0,
            pointSettings: {
                [vulnerabilityPillars['Physical']]:                  { "colour-index": 0, "colour-palette": "custom" },
                [vulnerabilityPillars['Psychological']]:             { "colour-index": 1, "colour-palette": "custom" },
                [vulnerabilityPillars['Economic']]:                  { "colour-index": 2, "colour-palette": "custom" },
                [vulnerabilityPillars['Social']]:                    { "colour-index": 3, "colour-palette": "custom" },
                [vulnerabilityPillars['Unspecified vulnerability']]: { "colour-index": 4, "colour-palette": "custom" },
            }
        };
    }

    function getVulnerabilityPillarTopics(pillarName, pillarId) {
        return {
            type: "CompTable",
            width: 4,
            height: 3,
            caption: `Most Prevalent topics: ${pillarName} Vulnerabilities`,
            filter: `segment is ${pillarId}`,
            rows: [
                {
                    id: 1,
                    groupBy: "topic",
                    limit: 10,
                    showOther: false,
                    showUnknown: false,
                }
            ],
            cols: [
                {
                    id: 1,
                    type: "mentionCount",
                    limit: 10,
                    showOther: true,
                    showUnknown: true,
                    showPercentage: true,
                    showBar: true,
                    showValue: true,
                    showDelta: true,
                },
                {
                    id: 3,
                    type: "authorIdCount",
                    limit: 10,
                    showOther: true,
                    showUnknown: true,
                    showPercentage: false,
                    showBar: true,
                    showValue: true,
                    showDelta: true,
                    "colour-palette": "be14",
                    "colour-index": 6,
                },
                {
                    id: 2,
                    type: "sentiment",
                    limit: 10,
                    showOther: true,
                    showUnknown: true,
                    showPercentage: false,
                    showBar: true,
                    showValue: true,
                    showDelta: true,
                    "colour-palette": "be14",
                    "colour-index": 6,
                }
            ],
        };
    }

    function getVulnerabilityPillarAuthors(pillarName, pillarId) {
        return {
            type: "CompTable",
            width: 4,
            height: 4,
            caption: `Top Authors: ${pillarName} Vulnerability`,
            filter: `segment is ${pillarId}`,
            rows: [
                {
                    id: 1,
                    groupBy: "authorId",
                    limit: 10,
                    showOther: false,
                    showUnknown: false,
                    name: "",
                }
            ],
            cols: [
                {
                    id: 1,
                    type: "mentionCount",
                    limit: 10,
                    showOther: true,
                    showUnknown: true,
                    showPercentage: false,
                    showBar: true,
                    showValue: true,
                    showDelta: true
                }
            ],
        };
    }

    vulnerability.metrics.push(
        {
            id: "volume/rows",
            title: "Volume by Vulnerabilities",
            description: "This chart shows how much each factor contributed to your Vulnerability-related conversation.",
            image: require("../assets/vulnerability/volume.png"),
            hero: true,
            widgetDesc: {
                type: "FantasticChart",
                xAxis: `${VULNERABILITY_SEGMENT_ID}`,
                yAxis: "mentionCount",
                width: 6,
                height: 4,
                geometry: "rows",
                caption: "Volume by vulnerabilities",
                "manual-caption": true,
                hideParents: true,
                filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                show: [
                    getVulnerabilityColourSettings("mentionCount", "rows")
                ]
            },
            options: [
                {
                    key: "reference",
                    title: "Reference",
                    tooltip: "View the vulnerability reference sheet",
                    action: () => ({
                        id: "reference",
                        widgetDesc: {
                            type: "Nomenclature",
                            caption: escapeHtml`Vulnerability reference sheet`,
                            width: 6,
                            height: 4,
                            selectedList: VULNERABILITY_SEGMENT_ID,
                            tooltip: "View the vulnerability reference sheet",
                            "hidden-title": true
                        }
                    })
                },
                {
                    key: "vulnerability/sentiment",
                    title: "Net Sentiment by Vulnerability factors",
                    action: () => ({
                        id: "sentiment/vulnerability",
                        widgetDesc: {
                            type: "FantasticChart",
                            xAxis: `${VULNERABILITY_SEGMENT_ID}`,
                            width: 6,
                            height: 4,
                            caption: "Net Sentiment by Vulnerabilities",
                            "manual-caption": true,
                            hideParents: true,
                            filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                            show: [
                                {
                                    yAxis: "totalSentimentPercent",
                                    geometry: "columns",
                                    "colour-index": 1
                                }
                            ]
                        }
                    })
                }
            ]
        },
        {
            id: "vulnerability/mentions",
            title: "Volume",
            description: "See how your volumes of Vulnerability-related conversations have changed",
            image: require("../assets/vulnerability/volumeComparison.png"),
            widgetDesc: {
                type: "FantasticChart",
                xAxis: "published",
                compare: `${VULNERABILITY_SEGMENT_ID}`,
                geometry: "columns",
                stacked: true,
                yAxis: "mentionPercent",
                width: 4,
                height: 4,
                caption: "Vulnerabilities comparison of volume",
                "manual-caption": true,
                hideParents: true,
                filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                show: [
                    getVulnerabilityColourSettings("mentionCount", "columns")
                ]
            },
            options: [
                {
                    key: "volume",
                    title: "Vulnerabilities comparison of volume",
                    tooltip: "See operational and reputational mention volumes of your brand and your competitors",
                    action: () => ({
                        id: "vulnerabilities/volumes",
                        widgetDesc: {
                            type: "FantasticChart",
                            xAxis: "published",
                            yAxis: "mentionCount",
                            compare: `${VULNERABILITY_SEGMENT_ID}`,
                            geometry: "columns",
                            stacked: true,
                            width: 8,
                            height: 4,
                            caption: "Vulnerabilities comparison of volume",
                            "manual-caption": true,
                            hideParents: true,
                            filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                            show: [
                                getVulnerabilityColourSettings("mentionCount", "lines")
                            ]
                        }
                    })
                },
                {
                    key: "vulnerability/volume-authors",
                    title: "Volume and number of Unique Authors",
                    action: () => ({
                        id: "vulnerability/volume-authors",
                        widgetDesc: {
                            type: "FantasticChart",
                            xAxis: "published",
                            yAxis: "mentionCount",
                            geometry: "columns",
                            stacked: true,
                            width: 8,
                            height: 4,
                            caption: "Volume and Number of unique authors",
                            "manual-caption": true,
                            hideParents: true,
                            filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                            show: [
                                {
                                    ...getVulnerabilityColourSettings("mentionCount", "columns"),
                                    "colour-index": 5,
                                    "colour-palette": "be14-gray",
                                },
                                {
                                    yAxis: "authorIdCount",
                                    geometry: "lines",
                                    "colour-palette": "be14-gray",
                                    "colour-index": 1,
                                    hideLabels: true,
                                }
                            ]
                        }
                    })
                },
            ]
        },
        {
            id: "vulnerability/sentiment-over-time",
            title: "Net Sentiment",
            description: "See Net Sentiment relating to Vulnerability-related conversations",
            image: require("../assets/vulnerability/net-sentiment.png"),
            widgetDesc: {
                type: "FantasticChart",
                xAxis: "published",
                yAxis: "mentionCount",
                geometry: "columns",
                width: 6,
                height: 4,
                caption: "Net Sentiment",
                "manual-caption": true,
                hideParents: true,
                filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                show: [
                    {
                        opacity: 70,
                        yAxis: "posAndNegPercent",
                        geometry: "columns",
                        "colour-palette": "be14-gray",
                        "colour-index": 5,
                    },
                    {
                        yAxis: "totalSentimentPercent",
                        geometry: "lines",
                        hideLabels: true,
                    }
                ]
            },
            options: [
                {
                    key: "vulnerability/sentiment-comparison",
                    title: "Sentiment comparison by vulnerabilities",
                    action: () => ({
                        id: "vulnerability/sentiment-comparison",
                        widgetDesc: {
                            type: "FantasticChart",
                            xAxis: `${VULNERABILITY_SEGMENT_ID}`,
                            yAxis: "mentionCount",
                            compare: "sentiment",
                            geometry: "columns",
                            width: 8,
                            height: 4,
                            caption: "Sentiment comparison by vulnerabilities",
                            "manual-caption": true,
                            hideParents: true,
                            show: [
                                {
                                    yAxis: "mentionPercent",
                                    geometry: "columns",
                                    "colour-index": 1,
                                }
                            ]
                        }
                    })
                },
                {
                    key: "vulnerability/sentiment-comparison-lines",
                    title: "Net sentiment comparison vulnerabilities",
                    action: () => ({
                        id: "vulnerability/sentiment-comparison-lines",
                        widgetDesc: {
                            type: "FantasticChart",
                            xAxis: "published",
                            yAxis: "mentionCount",
                            compare: `${VULNERABILITY_SEGMENT_ID}`,
                            geometry: "columns",
                            stacked: true,
                            width: 8,
                            height: 4,
                            caption: "Net Sentiment comparison Vulnerabilities",
                            "manual-caption": true,
                            hideParents: true,
                            filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                            show: [
                                getVulnerabilityColourSettings("totalSentimentPercent", "lines")
                            ]
                        }
                    })
                }
            ]
        },
        {
            id: "vulnerability/topic-wheel",
            title: "Topics",
            description: "See how vulnerability-related conversations break down by topic",
            image: require("../assets/vulnerability/topic-wheel.png"),
            widgetDesc: {
                type: "TopicWheel",
                width: "6",
                height: "4",
                caption: "Topic Wheel",
                "hidden-title": false,
                filter: `segment is ${VULNERABILITY_SEGMENT_ID}`
            },
            options: [
                {
                    key: "vulnerability/topics",
                    title: "Most Prevalent Topics",
                    tooltip: "See the most prevalent topics in your vulnerability-related conversations",
                    action: () => ({
                        id: "vulnerability/topics",
                        widgetDesc: {
                            type: "CompTable",
                            width: 4,
                            height: 3,
                            caption: "Most Prevalent topics: Vulnerabilities pillars",
                            "hidden-title": false,
                            filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                            rows: [
                                {
                                    id: 1,
                                    groupBy: "topic",
                                    limit: 10,
                                    showOther: false,
                                    showUnknown: false,
                                    sortByFirstCol: false
                                }
                            ],
                            cols: [
                                {
                                    id: 1,
                                    type: "mentionCount",
                                    limit: 10,
                                    showOther: true,
                                    showUnknown: true,
                                    showPercentage: true,
                                    showBar: true,
                                    showValue: true,
                                    showDelta: true,
                                },
                                {
                                    id: 3,
                                    type: "authorIdCount",
                                    limit: 10,
                                    showOther: true,
                                    showUnknown: true,
                                    showPercentage: false,
                                    showBar: true,
                                    showValue: true,
                                    showDelta: true,
                                    "colour-palette": "be14",
                                    "colour-index": 6,
                                },
                                {
                                    id: 2,
                                    type: "netSentiment",
                                    limit: 10,
                                    showOther: true,
                                    showUnknown: true,
                                    showPercentage: false,
                                    showBar: true,
                                    showValue: true,
                                    showDelta: true,
                                    "colour-palette": "be14",
                                    "colour-index": 6,
                                }
                            ],
                            swapRowsAndCols: false
                        }
                    })
                },
                {
                    key: "vulnerability/topics/physical",
                    title: "Most Prevalent Topics: Physical Vulnerability",
                    tooltip: "See the most prevalent topics in Physical vulnerability conversations",
                    action: () => ({
                        id: "vulnerability/topics/physical",
                        widgetDesc: getVulnerabilityPillarTopics("Physical", vulnerabilityPillars['Physical'])
                    })
                },
                {
                    key: "vulnerability/topics/social",
                    title: "Most Prevalent Topics: Social Vulnerability",
                    tooltip: "See the most prevalent topics in Social vulnerability conversations",
                    action: () => ({
                        id: "vulnerability/topics/social",
                        widgetDesc: getVulnerabilityPillarTopics("Social", vulnerabilityPillars['Social'])
                    })
                },
                {
                    key: "vulnerability/topics/economic",
                    title: "Most Prevalent Topics: Economic Vulnerability",
                    tooltip: "See the most prevalent topics in Economic vulnerability conversations",
                    action: () => ({
                        id: "vulnerability/topics/economic",
                        widgetDesc: getVulnerabilityPillarTopics("Economic", vulnerabilityPillars['Economic'])
                    })
                },
                {
                    key: "vulnerability/topics/psychological",
                    title: "Most Prevalent Topics: Psychological Vulnerability",
                    tooltip: "See the most prevalent topics in Psychological vulnerability conversations",
                    action: () => ({
                        id: "vulnerability/topics/psychological",
                        widgetDesc: getVulnerabilityPillarTopics("Psychological", vulnerabilityPillars['Psychological'])
                    })
                },
            ]
        },
        {
            id: "vulnerability/unique-authors",
            title: "Number of unique authors by vulnerabilities",
            description: "See the volume of unique authors who mention Vulnerability themes",
            image: require("../assets/vulnerability/authors-by-vuln.png"),
            widgetDesc: {
                type: "FantasticChart",
                xAxis: `${VULNERABILITY_SEGMENT_ID}`,
                yAxis: "mentionCount",
                geometry: "columns",
                width: 6,
                height: 4,
                caption: "Number of unique authors by vulnerabilities",
                "manual-caption": true,
                hideParents: true,
                filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                show: [
                    {
                        ...getVulnerabilityColourSettings("mentionCount", "columns"),
                    }
                ]
            },
            options: [
                {
                    key: "vulnerability/top-authors",
                    title: "Top Authors",
                    tooltip: "See the top authors in your vulnerability-related conversations",
                    action: () => ({
                        id: "vulnerability/top-authors",
                        widgetDesc: {
                            type: "CompTable",
                            width: 4,
                            height: 4,
                            caption: "Top Authors: Vulnerability",
                            "hidden-title": false,
                            filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                            rows: [
                                {
                                    id: 1,
                                    groupBy: "authorId",
                                    limit: 10,
                                    showOther: false,
                                    showUnknown: false,
                                    name: "",
                                    sortByFirstCol: false
                                }
                            ],
                            cols: [
                                {
                                    id: 1,
                                    type: "mentionCount",
                                    limit: 10,
                                    showOther: true,
                                    showUnknown: true,
                                    showPercentage: false,
                                    showBar: true,
                                    showValue: true,
                                    showDelta: true
                                }
                            ],
                            swapRowsAndCols: false
                        }
                    })
                },
                {
                    key: "vulnerability/top-authors/physical",
                    title: "Top Authors: Physical",
                    tooltip: "See the top authors in Physical vulnerability conversations",
                    action: () => ({
                        id: "vulnerability/top-authors/physical",
                        widgetDesc: getVulnerabilityPillarAuthors("Physical", vulnerabilityPillars['Physical'])
                    })
                },
                {
                    key: "vulnerability/top-authors/social",
                    title: "Top Authors: Social",
                    tooltip: "See the top authors in Social vulnerability conversations",
                    action: () => ({
                        id: "vulnerability/top-authors/social",
                        widgetDesc: getVulnerabilityPillarAuthors("Social", vulnerabilityPillars['Social'])
                    })
                },
                {
                    key: "vulnerability/top-authors/economic",
                    title: "Top Authors: Economic",
                    tooltip: "See the top authors in Economic vulnerability conversations",
                    action: () => ({
                        id: "vulnerability/top-authors/economic",
                        widgetDesc: getVulnerabilityPillarAuthors("Economic", vulnerabilityPillars['Economic'])
                    })
                },
                {
                    key: "vulnerability/top-authors/psychological",
                    title: "Top Authors: Psychological",
                    tooltip: "See the top authors in Psychological vulnerability conversations",
                    action: () => ({
                        id: "vulnerability/top-authors/psychological",
                        widgetDesc: getVulnerabilityPillarAuthors("Psychological", vulnerabilityPillars['Psychological'])
                    })
                },
            ]
        },
        {
            id: "vulnerability/social-network",
            title: "Volume by social network",
            description: "See the conversation volumes that you've received per social media platform",
            image: require("../assets/vulnerability/social-networks.png"),
            widgetDesc: {
                type: "FantasticChart",
                xAxis: "socialNetwork",
                yAxis: "mentionCount",
                geometry: "columns",
                stacked: true,
                width: 6,
                height: 4,
                caption: "Volume by social network",
                "manual-caption": true,
                hideParents: true,
                filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                show: [
                    {
                        ...getVulnerabilityColourSettings("mentionCount", "rows"),
                        "colour-index": 5,
                        "colour-palette": "be14-gray",
                    }
                ]
            },
            options: [
                {
                    key: "vulnerability/social-network/over-time",
                    title: "Social network comparison of volume",
                    tooltip: "See volume by social network over time",
                    action: () => ({
                        id: "vulnerability/social-network/over-time",
                        widgetDesc: {
                            type: "FantasticChart",
                            xAxis: "published",
                            yAxis: "mentionCount",
                            compare: "socialNetwork",
                            geometry: "columns",
                            stacked: true,
                            width: 8,
                            height: 4,
                            caption: "Social network comparison of volume",
                            "manual-caption": true,
                            hideParents: true,
                            filter: `segment is ${VULNERABILITY_SEGMENT_ID}`,
                            show: [
                                {
                                    ...getVulnerabilityColourSettings("mentionCount", "columns"),
                                    "colour-index": 5,
                                    "colour-palette": "be14-gray",
                                }
                            ]
                        }
                    })
                }
            ]
        }
    );
}